/**
 * accessibility.js — live regions, announcements, reduced-motion helpers
 * Loaded after theme.min.js; uses existing theme events.
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    // a11y: 2.3.3 — respect OS reduced motion for JS-driven carousels
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function announce(message) {
    // a11y: 4.1.3 status messages (#a11y-announcer in layout/theme.liquid; legacy ids supported)
    if (!message || typeof message !== 'string') return;
    var el =
      document.getElementById('a11y-announcer') ||
      document.getElementById('cart-live-region') ||
      document.getElementById('a11y-status-region');
    if (!el) return;
    el.textContent = '';
    window.setTimeout(function () {
      el.textContent = message;
    }, 100);
  }

  /** a11y: 2.4.1 — in-page # links move focus to target (skip links + CTAs, ACC-035) */
  function enhanceInternalHashLinks() {
    document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
      if (anchor.getAttribute('data-a11y-hash-bound') === '1') return;
      anchor.setAttribute('data-a11y-hash-bound', '1');
      anchor.addEventListener('click', function () {
        var href = anchor.getAttribute('href');
        if (!href || href.length < 2) return;
        var targetId = href.slice(1);
        if (!targetId) return;
        var target = document.getElementById(targetId);
        if (!target) return;
        window.setTimeout(function () {
          var mainId = 'MainContent';
          if (anchor.classList.contains('skip-link') && target.id === mainId) {
            target.classList.add('main-content--from-skip');
            var clearSkipRing = function () {
              target.classList.remove('main-content--from-skip');
              target.removeEventListener('blur', clearSkipRing);
            };
            target.addEventListener('blur', clearSkipRing);
          }
          if (!target.hasAttribute('tabindex')) {
            target.setAttribute('tabindex', '-1');
          }
          try {
            target.focus({ preventScroll: true });
          } catch (e) {
            if (typeof target.focus === 'function') target.focus();
          }
        }, 50);
      });
    });
  }

  /** a11y: ACC-024 — restore focus in cart drawer after quantity / update announcement */
  function focusCartDrawerAfterUpdate(detail) {
    window.setTimeout(function () {
      var cartDrawer =
        document.getElementById('CartDrawer') || document.getElementById('cart-drawer');
      if (!cartDrawer || cartDrawer.getAttribute('aria-hidden') === 'true') return;
      var updatedKey = detail && detail.lineKey;
      var changeType = detail && detail.changeType;
      var target = null;
      if (updatedKey && changeType === 'quantity') {
        var rows = cartDrawer.querySelectorAll('.cart__item[data-key]');
        for (var i = 0; i < rows.length; i++) {
          if (rows[i].getAttribute('data-key') === updatedKey) {
            target =
              rows[i].querySelector('.js-qty__num') ||
              rows[i].querySelector('input[type="number"]');
            break;
          }
        }
      }
      if (!target) {
        target =
          document.getElementById('CartDrawerTitle') ||
          cartDrawer.querySelector('.drawer__title');
      }
      if (!target || typeof target.focus !== 'function') return;
      if (!target.getAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    }, 150);
  }

  function attachLiveRegions() {
    document.addEventListener('ajaxProduct:added', function (evt) {
      // a11y: 4.1.3 — add to cart + subtotal (cart.js)
      var product = evt.detail && evt.detail.product;
      var title = (product && (product.product_title || product.title)) || '';
      function fallbackAnnounce() {
        var template = window.theme && theme.strings && theme.strings.a11yItemAdded;
        var msg = template ? template.replace(/\[\[item\]\]/g, title) : title;
        announce(msg || title || 'Item added to cart');
      }
      if (!window.theme || !theme.cart || typeof theme.cart.getCart !== 'function') {
        fallbackAnnounce();
        return;
      }
      theme.cart.getCart().then(function (cart) {
        var sub =
          cart && cart.total_price != null && theme.Currency
            ? theme.Currency.formatMoney(cart.total_price, theme.settings.moneyFormat)
            : '';
        var template = theme.strings && theme.strings.a11yItemAddedTotal;
        if (template && title && sub) {
          announce(
            template.replace(/\[\[item\]\]/g, title).replace(/\[\[total\]\]/g, sub)
          );
        } else {
          fallbackAnnounce();
        }
      }).catch(fallbackAnnounce);
    });

    document.addEventListener('ajaxProduct:error', function (evt) {
      // a11y: 4.1.3 — form / cart errors
      var err = evt.detail && evt.detail.errorMessage;
      if (err) announce(err);
    });

    document.addEventListener('cart:updated', function (evt) {
      // a11y: 4.1.3 — line remove / quantity / empty cart
      var detail = evt.detail || {};
      var cart = detail.cart;
      if (!cart) return;
      var sub =
        cart.total_price != null && window.theme && theme.Currency
          ? theme.Currency.formatMoney(cart.total_price, theme.settings.moneyFormat)
          : '';
      var lineTitle = detail.lineTitle || '';
      var qty =
        detail.lineQuantity != null ? String(detail.lineQuantity) : '';

      if (detail.changeType === 'remove' && lineTitle) {
        var removedTpl = theme.strings && theme.strings.a11yItemRemovedCart;
        var rmMsg = removedTpl
          ? removedTpl
              .replace(/\[\[item\]\]/g, lineTitle)
              .replace(/\[\[total\]\]/g, sub)
          : lineTitle + ' removed from cart. Subtotal ' + sub;
        if (cart.item_count === 0) {
          var emptyAfterRemove = theme.strings && theme.strings.a11yCartNowEmpty;
          if (emptyAfterRemove) {
            rmMsg += ' ' + emptyAfterRemove;
          }
        }
        announce(rmMsg);
        focusCartDrawerAfterUpdate(detail);
        return;
      }

      if (cart.item_count === 0) {
        announce(
          (theme.strings && theme.strings.a11yCartNowEmpty) ||
            'Your cart is now empty.'
        );
        focusCartDrawerAfterUpdate(detail);
        return;
      }

      if (detail.changeType === 'quantity' && lineTitle && qty) {
        var qtyTpl = theme.strings && theme.strings.a11yCartQuantityLine;
        announce(
          qtyTpl
            ? qtyTpl
                .replace(/\[\[item\]\]/g, lineTitle)
                .replace(/\[\[qty\]\]/g, qty)
                .replace(/\[\[total\]\]/g, sub)
            : lineTitle + ' quantity ' + qty + '. Subtotal ' + sub
        );
        focusCartDrawerAfterUpdate(detail);
        return;
      }

      var template = theme.strings && theme.strings.a11yCartUpdated;
      var count = cart.item_count != null ? String(cart.item_count) : '';
      var msg = template
        ? template.replace('[[count]]', count).replace('[[total]]', sub)
        : 'Cart updated, ' + count + ' items, total ' + sub;
      announce(msg);
      focusCartDrawerAfterUpdate(detail);
    });

    document.addEventListener('variant:change', function (evt) {
      // a11y: 4.1.3 PDP variant / price / availability updates for screen readers
      var v = evt.detail && evt.detail.variant;
      if (!window.theme || !theme.Currency || !theme.settings) return;
      var inStock =
        (window.theme.strings && theme.strings.a11yVariantInStock) || 'In stock.';
      var soldOut =
        (window.theme.strings && theme.strings.a11yVariantSoldOut) || 'Sold out.';
      var tmpl = window.theme.strings && theme.strings.a11yVariantUpdated;
      if (!v) {
        announce(
          (window.theme.strings && theme.strings.unavailable) || 'Unavailable'
        );
        return;
      }
      var priceStr = theme.Currency.formatMoney(v.price, theme.settings.moneyFormat);
      var avail = v.available ? inStock : soldOut;
      if (tmpl) {
        announce(
          tmpl
            .replace(/\[\[title\]\]/g, v.title || '')
            .replace(/\[\[price\]\]/g, priceStr)
            .replace(/\[\[availability\]\]/g, avail)
        );
      } else {
        announce((v.title || 'Variant') + '. ' + priceStr + '. ' + avail);
      }
    });

    document.addEventListener('recommendations:loaded', function () {
      var msg = window.theme && theme.strings && theme.strings.a11yRecommendationsLoaded;
      if (msg) announce(msg);
    });
  }

  /** a11y — SearchAtlas/Otto: crawler-only links stay in DOM for SEO but are hidden from AT and tab order */
  var ottoSealRafId;
  function sealOttoDynamicSeoIndexing() {
    document.querySelectorAll('div[data-otto-pixel="dynamic-seo"]').forEach(function (wrap) {
      wrap.setAttribute('aria-hidden', 'true');
      wrap.querySelectorAll('a.otto-dynamic-indexing-module').forEach(function (a) {
        a.setAttribute('tabindex', '-1');
      });
    });
  }
  function scheduleOttoDynamicSeoSeal() {
    if (ottoSealRafId) return;
    ottoSealRafId = requestAnimationFrame(function () {
      ottoSealRafId = null;
      sealOttoDynamicSeoIndexing();
    });
  }

  /** a11y: 2.3.3 — turn off Slick autoplay after init when user prefers reduced motion */
  function disableSlickAutoplayIfNeeded() {
    if (!prefersReducedMotion() || typeof window.jQuery === 'undefined') return;
    var $ = window.jQuery;
    if (!$.fn || !$.fn.slick) return;
    $('.slick-initialized').each(function () {
      try {
        $(this).slick('slickSetOption', 'autoplay', false, true);
      } catch (e) {
        /* ignore */
      }
    });
  }

  function looxRatingAriaLabel(rating, raters) {
    var n = parseFloat(String(rating), 10);
    if (isNaN(n)) return '';
    var rounded = Math.round(n * 10) / 10;
    var label = 'Rated ' + rounded + ' out of 5 stars';
    if (raters != null && String(raters).trim() !== '') {
      label += ', based on ' + String(raters).trim() + ' reviews';
    }
    return label;
  }

  function decorateLooxRating(container) {
    if (!container || !container.classList || !container.classList.contains('loox-rating')) return;
    var rating = container.getAttribute('data-rating');
    if (!rating) return;
    var parentLink = container.closest('a[href*="looxReviews"], a[href*="#looxReviews"]');
    var label = looxRatingAriaLabel(rating, container.getAttribute('data-raters'));
    container.querySelectorAll('img, svg').forEach(function (node) {
      node.setAttribute('aria-hidden', 'true');
    });
    if (parentLink && parentLink.getAttribute('aria-label')) {
      container.removeAttribute('role');
      container.removeAttribute('aria-label');
      container.setAttribute('aria-hidden', 'true');
      return;
    }
    if (!label) return;
    container.setAttribute('role', 'img');
    container.setAttribute('aria-label', label);
  }

  function scanLooxRatings(root) {
    var el = root && root.nodeType === 1 ? root : document.body;
    if (!el) return;
    var list = [];
    if (el.classList && el.classList.contains('loox-rating')) {
      list.push(el);
    }
    if (el.querySelectorAll) {
      list = list.concat([].slice.call(el.querySelectorAll('.loox-rating')));
    }
    list.forEach(decorateLooxRating);
  }

  function initLooxRatingObserver() {
    scanLooxRatings(document.body);
    if (!window.MutationObserver) return;
    var scheduled;
    var mo = new MutationObserver(function (mutations) {
      if (scheduled) return;
      scheduled = requestAnimationFrame(function () {
        scheduled = null;
        mutations.forEach(function (m) {
          if (m.type === 'childList') {
            m.addedNodes.forEach(function (node) {
              if (node.nodeType === 1) scanLooxRatings(node);
            });
          }
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Shopify Customer Privacy / cookie banner (#shopify-pc__banner) is injected late and uses
   * role="alertdialog" with aria-modal="false", which confuses SRs and validators. Treat it as
   * a named region, restore focusability, WCAG link targets, and one polite status announcement.
   */
  function linkOpensNewWindowPhrase() {
    return (
      (window.theme && theme.strings && theme.strings.a11yLinkOpensNewWindow) ||
      '(opens in new window)'
    );
  }

  function enhanceShopifyPrivacyCookieBanner(banner) {
    if (!banner || banner.nodeType !== 1 || banner.getAttribute('data-a11y-shopify-pc') === '1') {
      return;
    }
    banner.setAttribute('data-a11y-shopify-pc', '1');

    banner.setAttribute('role', 'region');
    banner.removeAttribute('aria-modal');

    if (!banner.hasAttribute('aria-labelledby')) {
      var h = banner.querySelector('h2');
      if (h && !h.id) {
        h.id = 'shopify-pc__banner__body-title';
      }
      if (h && h.id) {
        banner.setAttribute('aria-labelledby', h.id);
      }
    }

    banner.setAttribute('aria-live', 'polite');
    banner.setAttribute('aria-atomic', 'true');

    var policyLink = banner.querySelector(
      '#shopify-pc__banner__body-policy-link, .shopify-pc__banner__body a[target="_blank"]'
    );
    if (policyLink && policyLink.getAttribute('data-a11y-pc-newwin') !== '1') {
      policyLink.setAttribute('data-a11y-pc-newwin', '1');
      var hint = document.createElement('span');
      hint.className = 'visually-hidden';
      hint.textContent = ' ' + linkOpensNewWindowPhrase();
      policyLink.appendChild(hint);
    }

    banner.querySelectorAll('button, a[href]').forEach(function (el) {
      if (el.getAttribute('tabindex') === '-1') {
        el.removeAttribute('tabindex');
      }
    });

    var titleEl = banner.querySelector('#shopify-pc__banner__body-title, .shopify-pc__banner__body h2');
    var bodyEl = banner.querySelector('.shopify-pc__banner__body');
    var para = bodyEl && bodyEl.querySelector('p');
    var titleText = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';
    var paraText = para ? para.textContent.replace(/\s+/g, ' ').trim() : '';
    var msg = [titleText, paraText].filter(Boolean).join('. ');
    if (msg) {
      window.setTimeout(function () {
        announce(msg);
      }, 150);
    }
  }

  function initShopifyPrivacyCookieBannerA11y() {
    function run() {
      var banner = document.getElementById('shopify-pc__banner');
      if (banner) {
        enhanceShopifyPrivacyCookieBanner(banner);
      }
    }
    run();
    if (!window.MutationObserver) return;
    var scheduled;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = requestAnimationFrame(function () {
        scheduled = null;
        run();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function focusMainProductHeading() {
    if (!document.body || !document.body.classList.contains('template-product')) return;
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'back_forward') return;
    var h = document.querySelector(
      '#MainContent h1.product-single__title, #MainContent h2.product-single__title'
    );
    if (!h) return;
    h.setAttribute('tabindex', '-1');
    window.requestAnimationFrame(function () {
      try {
        h.focus({ preventScroll: true });
      } catch (e) {
        if (typeof h.focus === 'function') h.focus();
      }
    });
  }

  function enhanceAccordionThumbKeydown(el) {
    if (!el || el.tagName === 'BUTTON' || el.getAttribute('data-a11y-thumb-keys') === '1') return;
    el.setAttribute('data-a11y-thumb-keys', '1');
    if (!el.getAttribute('role')) el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    el.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      ev.preventDefault();
      el.click();
    });
  }

  function initAccordionThumbButtons() {
    document.querySelectorAll('.accordion-thumb:not(button)').forEach(enhanceAccordionThumbKeydown);
    if (!window.MutationObserver) return;
    new MutationObserver(function () {
      document.querySelectorAll('.accordion-thumb:not(button):not([data-a11y-thumb-keys])').forEach(enhanceAccordionThumbKeydown);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      attachLiveRegions();
      enhanceInternalHashLinks();
      scheduleOttoDynamicSeoSeal();
      initLooxRatingObserver();
      initShopifyPrivacyCookieBannerA11y();
      initAccordionThumbButtons();
      focusMainProductHeading();
    });
  } else {
    attachLiveRegions();
    enhanceInternalHashLinks();
    scheduleOttoDynamicSeoSeal();
    initLooxRatingObserver();
    initShopifyPrivacyCookieBannerA11y();
    initAccordionThumbButtons();
    focusMainProductHeading();
  }

  new MutationObserver(scheduleOttoDynamicSeoSeal).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  /** a11y: 2.4.3 — move SEA Accessibility widget and Gorgias chat before
   *  skip-links so tab order is: widget → chat → skip-to-content → page */
  function reorderA11yTabSequence() {
    var widget = document.getElementById('Sea-Accessibility__button');
    var skipNav = document.querySelector('nav.skip-links');
    var gorgiasContainer = document.getElementById('gorgias-chat-container');
    if (!skipNav) return;
    var parent = skipNav.parentNode;

    /* a11y: Enforce exact DOM order: widget → gorgias → skipNav.
       Always insert gorgias right before skipNav, then widget before gorgias,
       so the tab sequence is guaranteed regardless of when each element appears. */
    if (gorgiasContainer && gorgiasContainer.nextSibling !== skipNav) {
      parent.insertBefore(gorgiasContainer, skipNav);
    }
    if (widget && widget.nextSibling !== gorgiasContainer && widget.nextSibling !== skipNav) {
      parent.insertBefore(widget, gorgiasContainer || skipNav);
    }
  }
  reorderA11yTabSequence();
  new MutationObserver(function () {
    reorderA11yTabSequence();
  }).observe(document.body, { childList: true });

  window.addEventListener('load', function () {
    window.setTimeout(disableSlickAutoplayIfNeeded, 300);
    window.setTimeout(disableSlickAutoplayIfNeeded, 1200);
  });

  /** a11y: 4.1.2 dialog visibility for drawers/modals when open/closed */
  function patchOverlayAriaHidden() {
    if (!window.theme) return;
    if (theme.Drawers && theme.Drawers.prototype) {
      /* a11y: aria-hidden toggling for drawers is now handled directly
         in theme.min.js open/close methods (bind() made prototype
         patching ineffective) */
    }
    if (theme.Modals && theme.Modals.prototype) {
      var mOpen = theme.Modals.prototype.open;
      var mClose = theme.Modals.prototype.close;
      theme.Modals.prototype.open = function (evt) {
        if (this.modal) this.modal.setAttribute('aria-hidden', 'false');
        return mOpen.apply(this, arguments);
      };
      theme.Modals.prototype.close = function (evt) {
        var modalEl = this.modal;
        var ret = mClose.apply(this, arguments);
        window.setTimeout(function () {
          if (modalEl) modalEl.setAttribute('aria-hidden', 'true');
        }, 550);
        return ret;
      };
    }
  }
  patchOverlayAriaHidden();

  /** a11y: 2.1.2 / 4.1.2 PhotoSwipe zoom — focus trap, background inert via aria-hidden, restore focus */
  function enhancePhotoswipeProductZoom() {
    var pswp = document.querySelector('.pswp');
    if (!pswp || !window.MutationObserver) return;
    var wasOpen = false;
    var lastFocus = null;
    var pageShell = document.getElementById('PageContainer');

    function setBgHidden() {
      var bg = pswp.querySelector('.pswp__bg');
      if (bg) bg.setAttribute('aria-hidden', 'true');
    }

    setBgHidden();

    new MutationObserver(function () {
      var open = pswp.classList.contains('pswp--open');
      if (open && !wasOpen) {
        wasOpen = true;
        lastFocus = document.activeElement;
        pswp.setAttribute('aria-hidden', 'false');
        if (pageShell) pageShell.setAttribute('aria-hidden', 'true');
        var closeBtn = pswp.querySelector('.pswp__button--close');
        if (window.theme && theme.a11y && theme.a11y.trapFocus) {
          theme.a11y.trapFocus({
            container: pswp,
            elementToFocus: closeBtn || undefined,
            namespace: 'photoswipe-pdp',
          });
        } else if (closeBtn && closeBtn.focus) {
          closeBtn.focus();
        }
      } else if (!open && wasOpen) {
        wasOpen = false;
        pswp.setAttribute('aria-hidden', 'true');
        if (pageShell) pageShell.removeAttribute('aria-hidden');
        if (window.theme && theme.a11y && theme.a11y.removeTrapFocus) {
          theme.a11y.removeTrapFocus({ container: pswp, namespace: 'photoswipe-pdp' });
        }
        if (lastFocus && typeof lastFocus.focus === 'function') {
          try {
            lastFocus.focus();
          } catch (e) {
            /* ignore */
          }
        }
      }
    }).observe(pswp, { attributes: true, attributeFilter: ['class'] });
  }

  function enhanceMobileAccordionAria() {
    document.addEventListener(
      'click',
      function (e) {
        var thumb = e.target && e.target.closest && e.target.closest('.accordion-custom-section .accordion-thumb');
        if (!thumb) return;
        window.setTimeout(function () {
          var item = thumb.closest('.accordion-item');
          if (!item) return;
          var open = item.classList.contains('is-active');
          thumb.setAttribute('aria-expanded', open ? 'true' : 'false');
          var panel = item.querySelector('.accordion-panel');
          if (panel) panel.setAttribute('aria-hidden', open ? 'false' : 'true');
        }, 450);
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      enhancePhotoswipeProductZoom();
      enhanceMobileAccordionAria();
    });
  } else {
    enhancePhotoswipeProductZoom();
    enhanceMobileAccordionAria();
  }
})();

